import { AxiosRequestConfig } from 'axios';
import { cachedAuthGet, CacheConfig, processApiError } from './api_utils';

/**
 * Higher-order function to add caching to existing API functions
 * @param apiFunction The original API function
 * @param defaultCacheConfig Default cache configuration for this function
 * @returns A new function with caching capabilities
 */
export function withCache<TParams, TResponse>(
  apiFunction: (params: TParams) => Promise<TResponse>,
  defaultCacheConfig: CacheConfig = {}
) {
  return async (
    params: TParams,
    cacheConfig: CacheConfig = {}
  ): Promise<TResponse> => {
    const mergedConfig = { ...defaultCacheConfig, ...cacheConfig };

    // For now, we'll call the original function
    // In a full implementation, you'd need to extract the URL and config from the original function
    return apiFunction(params);
  };
}

/**
 * Creates a cached API function for GET requests
 * @param url The API endpoint URL
 * @param defaultCacheConfig Default cache configuration
 * @returns A function that makes cached GET requests
 */
export function createCachedApiFunction<TParams extends Record<string, any> = Record<string, any>, TResponse = any>(
  url: string,
  defaultCacheConfig: CacheConfig = {}
) {
  return async (
    params: TParams = {} as TParams,
    cacheConfig: CacheConfig = {}
  ): Promise<TResponse> => {
    const mergedConfig = { ...defaultCacheConfig, ...cacheConfig };

    // Convert params to query parameters
    const queryParams: Record<string, any> = { ...params };

    // Remove undefined params
    Object.keys(queryParams).forEach(key =>
      queryParams[key] === undefined && delete queryParams[key]
    );

    const axiosConfig: AxiosRequestConfig = {
      params: queryParams
    };

    try {
      return await cachedAuthGet<TResponse>(url, axiosConfig, mergedConfig);
    } catch (error) {
      throw error; // Let the calling function handle error processing
    }
  };
}

/**
 * Standardized cache key generation function
 * Removes base URLs and creates consistent cache keys across the application
 */
export function generateStandardCacheKey(
  url: string,
  params?: any,
  headers?: any,
  method: string = 'GET'
): string {
  // Extract endpoint path from full URL, removing base URL
  const cleanEndpoint = extractEndpointPath(url);

  // Sort and stringify params for consistency
  // Exclude export_format from cache keys as exports should never be cached
  const sortedParams = params && Object.keys(params).length > 0
    ? Object.keys(params)
      .filter(key => key !== 'export_format') // Exclude export_format from cache key
      .sort()
      .reduce((result, key) => {
        if (params[key] !== undefined && params[key] !== null) {
          result[key] = params[key];
        }
        return result;
      }, {} as any)
    : {};

  const paramsStr = Object.keys(sortedParams).length > 0
    ? JSON.stringify(sortedParams)
    : '';

  // Filter out sensitive headers from cache key
  let filteredHeaders = {};
  if (headers) {
    filteredHeaders = Object.keys(headers)
      .filter(key =>
        // Exclude sensitive headers
        !['authorization', 'Authorization', 'bearer', 'Bearer'].includes(key) &&
        !key.toLowerCase().includes('token') &&
        !key.toLowerCase().includes('auth')
      )
      .reduce((obj, key) => {
        obj[key] = headers[key];
        return obj;
      }, {} as any);
  }

  const headersStr = Object.keys(filteredHeaders).length > 0
    ? JSON.stringify(filteredHeaders)
    : '';

  // Create cache key in format: endpoint:params:headers
  const parts = [cleanEndpoint];
  if (paramsStr) parts.push(paramsStr);
  if (headersStr) parts.push(headersStr);

  return parts.join(':');
}

/**
 * Extracts clean endpoint path from full URL
 * Removes protocol, domain, port, and keeps only the API path
 */
export function extractEndpointPath(url: string): string {
  try {
    let path: string;

    // Handle relative URLs
    if (!url.includes('://')) {
      path = url.startsWith('/') ? url.substring(1) : url;
    } else {
      // Parse full URL and extract pathname
      const urlObj = new URL(url);
      path = urlObj.pathname;

      // Remove leading slash
      if (path.startsWith('/')) {
        path = path.substring(1);
      }
    }

    // Remove common API prefixes for cleaner keys
    if (path.startsWith('api/v1/')) {
      path = path.substring(7);
    } else if (path.startsWith('api/')) {
      path = path.substring(4);
    }

    return path;
  } catch (error) {
    // Fallback: return original URL without protocol/domain
    return url.replace(/^https?:\/\/[^\/]+\/?/, '');
  }
}

/**
 * Utility to create cache keys for specific API endpoints
 */
export const createCacheKey = {
  /**
   * Creates a cache key for list endpoints
   */
  forList: (endpoint: string, params: any = {}) => {
    const cleanEndpoint = extractEndpointPath(endpoint);
    const sortedParams = Object.keys(params)
      .filter(key => key !== 'export_format') // Exclude export_format from cache key
      .sort()
      .reduce((result, key) => {
        result[key] = params[key];
        return result;
      }, {} as any);

    return `${cleanEndpoint}:list:${JSON.stringify(sortedParams)}`;
  },

  /**
   * Creates a cache key for detail endpoints
   */
  forDetail: (endpoint: string, id: string | number) => {
    const cleanEndpoint = extractEndpointPath(endpoint);
    return `${cleanEndpoint}:detail:${id}`;
  },

  /**
   * Creates a cache key for summary/statistics endpoints
   */
  forSummary: (endpoint: string, params: any = {}) => {
    const cleanEndpoint = extractEndpointPath(endpoint);
    const sortedParams = Object.keys(params)
      .filter(key => key !== 'export_format') // Exclude export_format from cache key
      .sort()
      .reduce((result, key) => {
        result[key] = params[key];
        return result;
      }, {} as any);

    return `${cleanEndpoint}:summary:${JSON.stringify(sortedParams)}`;
  },

  /**
   * Creates a standardized cache key using the new generation function
   */
  standard: (url: string, params?: any, headers?: any, method: string = 'GET') => {
    return generateStandardCacheKey(url, params, headers, method);
  }
};

/**
 * Common cache configurations for different types of data
 */
export const cacheConfigs = {
  /** For frequently changing data (5 minutes) - uses sessionStorage */
  short: { duration: 5, storage: 'sessionStorage' as const },

  /** For moderately changing data (20 minutes - default) - uses sessionStorage */
  medium: { duration: 20, storage: 'sessionStorage' as const },

  /** For rarely changing data (1 hour) - uses sessionStorage */
  long: { duration: 60, storage: 'sessionStorage' as const },

  /** For very stable data (4 hours) - uses sessionStorage */
  stable: { duration: 240, storage: 'sessionStorage' as const },

  /** For session-only data (20 minutes) - uses sessionStorage */
  session: { duration: 20, storage: 'sessionStorage' as const },

  /** For memory-only data (5 minutes) - uses memory cache */
  memory: { duration: 5, storage: 'memory' as const },

  /** Force refresh - uses sessionStorage */
  force: { force: true, storage: 'sessionStorage' as const },

  /** No cache - uses memory with 0 duration */
  none: { duration: 0, storage: 'memory' as const }
};

/**
 * Storage-specific cache configurations
 */
export const storageConfigs = {
  /** Persistent cache configurations using localStorage */
  persistent: {
    short: { duration: 5, storage: 'localStorage' as const },
    medium: { duration: 20, storage: 'localStorage' as const },
    long: { duration: 60, storage: 'localStorage' as const },
    stable: { duration: 240, storage: 'localStorage' as const }
  },

  /** Session-only cache configurations using sessionStorage */
  session: {
    short: { duration: 5, storage: 'sessionStorage' as const },
    medium: { duration: 20, storage: 'sessionStorage' as const },
    long: { duration: 60, storage: 'sessionStorage' as const }
  },

  /** Memory-only cache configurations */
  memory: {
    short: { duration: 2, storage: 'memory' as const },
    medium: { duration: 5, storage: 'memory' as const },
    long: { duration: 10, storage: 'memory' as const }
  }
};

/**
 * Cache invalidation patterns for different data types
 */
export const cachePatterns = {
  /** Invalidate all company-related data */
  company: '/api/v1/company',

  /** Invalidate all property-related data */
  properties: '/api/v1/properties',

  /** Invalidate all transaction-related data */
  transactions: '/api/v1/transactions',

  /** Invalidate all user-related data */
  users: '/api/v1/users',

  /** Invalidate all payment-related data */
  payments: '/api/v1/company/payments',

  /** Invalidate all service-related data */
  services: '/api/v1/services',

  /** Invalidate all account payables data */
  payablesBills: '/api/account-payables/bills',
  payablesPayments: '/api/account-payables/payments',
  payablesFees: '/api/reports/v1/transaction-fees', // Fixed: was /api/account-payables/fees
  payablesStatements: '/api/account-payables/statements',
  payablesMissedBills: '/api/account-payables/missed-bills',
  bankAccounts: '/api/account-payables/bank-accounts',
  glCodes: '/api/account-payables/gl-codes',
  spikeThreshold: '/api/account-payables/settings/spike-threshold',

  /** Invalidate all account receivables data */
  receivables: '/api/account-receivables/receivables',
  billBack: '/api/account-receivables/bill-back'
};

/**
 * Utility functions for cache management
 */
export const cacheUtils = {
  /**
   * Creates a cache configuration for user-specific data
   * @param userId User ID to include in cache key
   * @param baseConfig Base cache configuration
   */
  forUser: (userId: string | number, baseConfig: CacheConfig = cacheConfigs.medium): CacheConfig => ({
    ...baseConfig,
    key: baseConfig.key ? `user_${userId}_${baseConfig.key}` : `user_${userId}`
  }),

  /**
   * Creates a cache configuration for company-specific data
   * @param companyId Company ID to include in cache key
   * @param baseConfig Base cache configuration
   */
  forCompany: (companyId: string | number, baseConfig: CacheConfig = cacheConfigs.medium): CacheConfig => ({
    ...baseConfig,
    key: baseConfig.key ? `company_${companyId}_${baseConfig.key}` : `company_${companyId}`
  }),

  /**
   * Creates a cache configuration for property-specific data
   * @param propertyId Property ID to include in cache key
   * @param baseConfig Base cache configuration
   */
  forProperty: (propertyId: string | number, baseConfig: CacheConfig = cacheConfigs.medium): CacheConfig => ({
    ...baseConfig,
    key: baseConfig.key ? `property_${propertyId}_${baseConfig.key}` : `property_${propertyId}`
  }),

  /**
   * Creates a cache configuration with environment-specific key
   * @param environment Environment name (dev, staging, prod)
   * @param baseConfig Base cache configuration
   */
  forEnvironment: (environment: string, baseConfig: CacheConfig = cacheConfigs.medium): CacheConfig => ({
    ...baseConfig,
    key: baseConfig.key ? `env_${environment}_${baseConfig.key}` : `env_${environment}`
  }),

  /**
   * Creates a cache configuration for time-sensitive data
   * @param timeWindow Time window identifier (e.g., 'daily', 'weekly', 'monthly')
   * @param baseConfig Base cache configuration
   */
  forTimeWindow: (timeWindow: string, baseConfig: CacheConfig = cacheConfigs.medium): CacheConfig => ({
    ...baseConfig,
    key: baseConfig.key ? `time_${timeWindow}_${baseConfig.key}` : `time_${timeWindow}`
  })
};

/**
 * Cache strategies for different use cases
 */
export const cacheStrategies = {
  /** For real-time data that changes frequently */
  realtime: {
    ...cacheConfigs.medium,
    storage: 'memory' as const,
    duration: 1 // 1 minute
  },

  /** For user interface state */
  ui: {
    ...cacheConfigs.medium,
    duration: 30 // 30 minutes
  },

  /** For reference data that rarely changes */
  reference: {
    ...cacheConfigs.medium,
    duration: 720 // 12 hours
  },

  /** For analytics data */
  analytics: {
    ...cacheConfigs.medium,
    duration: 15 // 15 minutes
  },

  /** For user preferences */
  preferences: {
    ...cacheConfigs.medium,
    storage: 'localStorage' as const,
    duration: 1440 // 24 hours
  }
}; 